"use client";

import { DAYS_OF_WEEK, VenueHoursDisplay } from "./VenueHours";
import { Field, Form } from "react-final-form";
import { formatTimeRange, parseTimeRange } from "./utils";

import { PencilIcon } from "lucide-react";
import { PunchupButton } from "@punchup/ui-common/components/common";
import { cn } from "@punchup/utils/cn";
import { z } from "zod";

const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;

const validationSchema = z.object({
  hours: z.record(
    z.string(),
    z.union([
      z.literal("Closed"),
      z.object({
        open: z
          .string()
          .regex(timeRegex, { message: "Invalid time format" })
          .min(1, { message: "Opening time is required" }),
        close: z
          .string()
          .regex(timeRegex, { message: "Invalid time format" })
          .min(1, { message: "Closing time is required" }),
      }),
    ])
  ),
});

type VenueHoursFormValues = z.infer<typeof validationSchema>;

// Helper function to set nested values based on an array of keys.
const setIn = (
  obj: Record<string, any>,
  path: (string | number)[],
  value: any
) => {
  let current = obj;
  for (let i = 0; i < path.length; i++) {
    const key = path[i];
    if (i === path.length - 1) {
      current[key] = value;
    } else {
      if (typeof current[key] !== "object" || current[key] === null) {
        current[key] = {};
      }
      current = current[key];
    }
  }
};

const validate = (values: VenueHoursFormValues) => {
  try {
    validationSchema.parse(values);
    return {};
  } catch (e) {
    if (e instanceof z.ZodError) {
      const errors: Record<string, any> = {};
      e.errors.forEach((error) => {
        setIn(errors, error.path, error.message);
      });
      return errors;
    }
    return {};
  }
};

interface VenueHoursFormProps {
  hours: Record<string, string> | null;
  isEditing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSubmit: (values: Record<string, string>) => void;
}

const sharedStyles = cn(
  "text-slate-gray-900 p-2 md:p-3 block w-full rounded-lg border border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50",
  "[&::-webkit-calendar-picker-indicator]:cursor-pointer"
);

export const VenueHoursForm: React.FC<VenueHoursFormProps> = ({
  hours,
  isEditing,
  onEdit,
  onCancel,
  onSubmit,
}) => {
  if (!isEditing) {
    return (
      <>
        <div className="space-y-2">
          <VenueHoursDisplay hours={hours as Record<string, string> | null} />
        </div>
        <PunchupButton
          onClick={onEdit}
          className="flex items-center gap-2 w-fit"
        >
          <PencilIcon className="h-4 w-4" /> Edit Hours
        </PunchupButton>
      </>
    );
  }

  // Build the initial form values. For each day, if the current value is "Closed"
  // return "Closed", otherwise parse the time range.
  const initialHours = DAYS_OF_WEEK.reduce(
    (acc, day) => {
      if (hours?.[day] === "Closed") {
        acc[day] = "Closed";
      } else {
        acc[day] = parseTimeRange(hours?.[day] || null) as {
          open: string;
          close: string;
        };
      }
      return acc;
    },
    {} as Record<string, { open: string; close: string } | "Closed">
  );

  return (
    <Form
      onSubmit={(values: VenueHoursFormValues) => {
        const formattedHours = Object.entries(values.hours).reduce(
          (acc, [day, times]) => {
            if (times === "Closed") {
              acc[day] = "Closed";
            } else {
              acc[day] = formatTimeRange(times.open, times.close);
            }
            return acc;
          },
          {} as Record<string, string>
        );
        onSubmit(formattedHours);
      }}
      initialValues={{ hours: initialHours }}
      validate={validate}
      render={({
        handleSubmit,
        pristine,
        form,
        values,
        valid,
        initialValues,
        hasSubmitErrors,
        hasValidationErrors,
      }) => (
        <form onSubmit={handleSubmit} className="space-y-4">
          {DAYS_OF_WEEK.map((day) => {
            const dayValue = values.hours[day];
            const isClosed = dayValue === "Closed";
            return (
              <div key={day}>
                <label className="block text-sm font-medium text-gray-700">
                  {day}
                </label>
                {isClosed ? (
                  <Field
                    name={`hours.${day}`}
                    subscription={{
                      value: true,
                      error: true,
                      touched: true,
                      dirty: true,
                    }}
                  >
                    {({ input }) => (
                      <div className="flex items-center justify-between mt-1">
                        <span className="italic text-gray-500">Closed</span>
                        <PunchupButton
                          type="button"
                          variant="plain"
                          onClick={() => {
                            const initialDayValues = initialValues.hours[day];
                            input.onChange({
                              open:
                                typeof initialDayValues === "object"
                                  ? initialDayValues.open
                                  : "",
                              close:
                                typeof initialDayValues === "object"
                                  ? initialDayValues.close
                                  : "",
                            });
                          }}
                          className="ml-2"
                        >
                          Mark as Open
                        </PunchupButton>
                      </div>
                    )}
                  </Field>
                ) : (
                  <>
                    <div className="flex gap-x-4 mt-1">
                      <Field
                        name={`hours.${day}.open`}
                        subscription={{
                          value: true,
                          error: true,
                          touched: true,
                          dirty: true,
                        }}
                      >
                        {({ input, meta }) => (
                          <div className="w-full">
                            <label className="block text-sm text-gray-600 mb-1">
                              Opening Hours
                            </label>
                            <input
                              {...input}
                              type="time"
                              className={cn(
                                sharedStyles,
                                meta.error &&
                                  (meta.touched || meta.dirty) &&
                                  "border-red-500"
                              )}
                              placeholder="Opening time"
                            />
                            {meta.error && (meta.touched || meta.dirty) && (
                              <span className="text-red-500 text-sm block mt-1">
                                {meta.error}
                              </span>
                            )}
                          </div>
                        )}
                      </Field>
                      <span className="flex items-start mt-8 md:mt-9">to</span>
                      <Field
                        name={`hours.${day}.close`}
                        subscription={{
                          value: true,
                          error: true,
                          touched: true,
                          dirty: true,
                        }}
                      >
                        {({ input, meta }) => (
                          <div className="w-full">
                            <label className="block text-sm text-gray-600 mb-1">
                              Closing Hours
                            </label>
                            <input
                              {...input}
                              type="time"
                              className={cn(
                                sharedStyles,
                                meta.error &&
                                  (meta.touched || meta.dirty) &&
                                  "border-red-500"
                              )}
                              placeholder="Closing time"
                            />
                            {meta.error && (meta.touched || meta.dirty) && (
                              <span className="text-red-500 text-sm block mt-1">
                                {meta.error}
                              </span>
                            )}
                          </div>
                        )}
                      </Field>
                    </div>
                    <Field
                      name={`hours.${day}`}
                      subscription={{
                        value: true,
                        error: true,
                        touched: true,
                        dirty: true,
                      }}
                    >
                      {({ input }) => (
                        <PunchupButton
                          type="button"
                          variant="plain"
                          onClick={() => {
                            input.onChange("Closed");
                          }}
                          className="mt-2"
                        >
                          Mark as Closed
                        </PunchupButton>
                      )}
                    </Field>
                  </>
                )}
              </div>
            );
          })}

          <div className="flex items-center gap-x-4">
            <PunchupButton
              type="button"
              variant="plain"
              onClick={() => {
                form.reset();
                onCancel();
              }}
            >
              Cancel
            </PunchupButton>
            <PunchupButton type="submit" disabled={pristine || !valid}>
              Save
            </PunchupButton>
          </div>
        </form>
      )}
    />
  );
};
